import { motion } from 'framer-motion'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faLinkedin, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import {
  Phone,
  Mail,
  MapPin,
  Star,
  ArrowRight,
  Shield,
  Award,
  Users,
  ChevronUp,
} from 'lucide-react'

import logo from '../../assets/logo_white.png'
import { useNavigate } from 'react-router'

export function Footer1() {
  const navigation = useNavigate()
  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Services', href: '/services' },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'Pricing', href: '/price' },
    { label: 'Contact', href: '/contact' },
  ]

  const services = [
    { label: 'Lawn Maintenance', href: '/services' },
    { label: 'Garden Design', href: '/services' },
    { label: 'Tree Services', href: '/services' },
    { label: 'Hardscaping', href: '/services' },
    { label: 'Irrigation Systems', href: '/services' },
    { label: 'Seasonal Cleanup', href: '/services' },
  ]

  const serviceAreas = [
    'Beverly Hills',
    'Santa Monica',
    'Pasadena',
    'West Hollywood',
    'Manhattan Beach',
    'Malibu',
    'Glendale',
    'Burbank',
  ]

  const socialLinks = [
    { icon: <FontAwesomeIcon icon={faFacebook} size="lg" color="#FFFFFF" />, href: '#', label: 'Facebook' },
    { icon: <FontAwesomeIcon icon={faInstagram} size="lg" color="#FFFFFF" />, href: '#', label: 'Instagram' },
    { icon: <FontAwesomeIcon icon={faXTwitter} size="lg" color="#FFFFFF" />, href: '#', label: 'Twitter' },
    { icon: <FontAwesomeIcon icon={faLinkedin} size="lg" color="#FFFFFF" />, href: '#', label: 'LinkedIn' },
    { icon: <FontAwesomeIcon icon={faYoutube} size="lg" color="#FFFFFF" />, href: '#', label: 'YouTube' },
  ]

  const certifications = [
    { icon: <Shield className="w-5 h-5" />, text: 'Licensed & Insured' },
    { icon: <Award className="w-5 h-5" />, text: 'ISA Certified Arborist' },
    { icon: <Star className="w-5 h-5" />, text: 'BBB A+ Rating' },
    { icon: <Users className="w-5 h-5" />, text: '500+ Happy Clients' },
  ]

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const scrollToSection = (href: string) => {
    navigation(href.replace('#', '/').replace('home', ''))
    window.scrollTo(0, 0)

    return
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <footer className="bg-green-800 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
          <defs>
            <pattern
              id="footer-pattern"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="10" cy="10" r="1" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#footer-pattern)" />
        </svg>
      </div>

      <div className="relative z-10">
        {/* Newsletter Section */}
        <motion.div
          className="bg-green-700 py-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">
                Stay Updated with Seasonal Tips & Offers
              </h3>
              <p className="text-green-100 mb-8 max-w-2xl mx-auto">
                Subscribe to our newsletter for expert landscaping tips,
                seasonal advice, and exclusive offers for our valued customers.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto mb-6">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 h-[38px] px-6 py-3 rounded-lg text-white border-1 focus:ring-2 focus:ring-green-400 focus:outline-none  border-white placeholder-white"
                />
                <Button className="bg-green-600 h-[40px] hover:bg-green-500 text-white  px-8 py-3 whitespace-nowrap">
                  Subscribe
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>

              <p className="text-green-200 text-sm">
                No spam, unsubscribe at any time. Join 2,000+ homeowners who
                trust our advice.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Main Footer Content */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
              {/* Company Info */}
              <motion.div
                className="lg:col-span-1"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Logo */}
                <div className="flex items-center space-x-3 mb-6">
                  <img src={logo} alt="logo-w" className="h-12" />
                </div>

                <p className="text-green-100 mb-6 leading-relaxed">
                  Creating beautiful, sustainable landscapes for over 15 years.
                  We transform ordinary outdoor spaces into extraordinary
                  environments that enhance your property and lifestyle.
                </p>

                {/* Contact Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-3 text-green-400" />
                    <a
                      href="tel:+15551234567"
                      className="text-green-100 hover:text-white transition-colors"
                    >
                      (555) 123-4567
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-3 text-green-400" />
                    <a
                      href="mailto:demo@example.com"
                      className="text-green-100 hover:text-white transition-colors"
                    >
                      demo@example.com
                    </a>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 mr-3 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-green-100">
                      123 Garden Lane
                      <br />
                      Green Valley, CA 90210
                    </span>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      className="w-10 h-10 bg-green-700 hover:bg-green-600 rounded-lg flex items-center justify-center transition-colors"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </motion.div>

              {/* Quick Links */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
                <ul className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className="text-green-100 hover:text-white transition-colors flex items-center group"
                      >
                        <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" />
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <h5 className="font-medium mb-4 text-green-200">
                    Business Hours
                  </h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-100">Mon - Fri</span>
                      <span className="text-white">7:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-100">Saturday</span>
                      <span className="text-white">8:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-100">Sunday</span>
                      <span className="text-white">Closed</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Services */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h4 className="text-lg font-semibold mb-6">Our Services</h4>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={index}>
                      <button
                        onClick={() => scrollToSection(service.href)}
                        className="text-green-100 hover:text-white transition-colors flex items-center group"
                      >
                        <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" />
                        {service.label}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <Badge className="bg-green-600 text-white mb-4">
                    Emergency Services Available
                  </Badge>
                  <p className="text-green-100 text-sm">
                    24/7 emergency tree removal and storm damage cleanup
                    services available.
                  </p>
                </div>
              </motion.div>

              {/* Service Areas */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h4 className="text-lg font-semibold mb-6">Service Areas</h4>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {serviceAreas.map((area, index) => (
                    <span key={index} className="text-green-100 text-sm">
                      {area}
                    </span>
                  ))}
                </div>

                <p className="text-green-100 text-sm mb-6">
                  Serving Los Angeles County and surrounding areas. Call us to
                  confirm service availability in your location.
                </p>

                {/* Certifications */}
                <div className="space-y-3">
                  <h5 className="font-medium text-green-200">
                    Certifications & Awards
                  </h5>
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center">
                      <span className="text-green-400 mr-3">{cert.icon}</span>
                      <span className="text-green-100 text-sm">
                        {cert.text}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-green-700 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col xl:flex-row justify-between items-center gap-4">
              <div className="flex flex-col xl:flex-row items-center gap-4 text-sm text-green-200">
                <span>
                  Copyright © 2025, Gardenly. All Right Reserved.
                </span>
                <div className="flex space-x-6">
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                  <a href="#" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                  <a href="#" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <span className="text-green-200 text-sm">
                  License #: LC123456
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scrollToTop}
                  className="border-green-600 text-white hover:bg-green-600 hover:text-white"
                >
                  Back to Top
                  <ChevronUp className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
